"use client";

import { useEffect, useMemo, useState } from "react";

import i18n from "@/i18n";
import { DirectionProvider } from "@/components/ui/direction";
import {
  isRtlLanguage,
  normalizeLanguage,
  readStoredLanguage,
} from "@/i18n/settings";

function applyDocumentLanguage(language: string) {
  const normalized = normalizeLanguage(language);
  const rtl = isRtlLanguage(normalized);

  document.documentElement.lang = normalized;
  document.documentElement.dir = rtl ? "rtl" : "ltr";
}

export function LocaleDirectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState(
    () =>
      readStoredLanguage() ??
      normalizeLanguage(i18n.resolvedLanguage ?? i18n.language),
  );

  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  useEffect(() => {
    const onLanguageChanged = (nextLanguage: string) => {
      setLanguage(normalizeLanguage(nextLanguage));
    };

    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  const direction = useMemo(
    () => (isRtlLanguage(language) ? "rtl" : "ltr"),
    [language],
  );

  return <DirectionProvider direction={direction}>{children}</DirectionProvider>;
}
